import React, { useState, useEffect } from 'react';
import { CarpoolVehicle } from '../types';
import { Car, TrendingUp, AlertTriangle, Users, User, Euro, Calendar, Shield } from 'lucide-react';
import { filterActiveVehicles } from '../utils/contractUtils';
import baserowApi from '../services/baserowApi';

interface StatsOverviewProps {
  vehicles: CarpoolVehicle[];
}

interface DamageReport {
  id: number;
  title: string;
  description?: string;
  status: { id: number; value: string; color: string };
  carpool: Array<{ id: number; value: string }>;
  estimated_costs: string;
  real_costs: string;
  created_at?: string;
}

// Kategorien für Projekte
const CATEGORY_OPTIONS = [
  'Schaden',
  'Termin', 
  'Service & Inspektion',
] as const;
type ProjectCategory = typeof CATEGORY_OPTIONS[number];

function extractCategoryFromDescription(description: string): ProjectCategory | null {
  if (!description) return null;
  const match = description.match(/^Kategorie:\s*(.+)$/m);
  if (match) {
    const value = match[1].trim();
    if ((CATEGORY_OPTIONS as readonly string[]).includes(value)) {
      return value as ProjectCategory;
    }
  }
  return null;
}

const StatsOverview: React.FC<StatsOverviewProps> = ({ vehicles }) => {
  const [damageReports, setDamageReports] = useState<DamageReport[]>([]);
  const [totalDamageCosts, setTotalDamageCosts] = useState(0);
  const [totalProjectsByCategory, setTotalProjectsByCategory] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);

  const activeVehicles = filterActiveVehicles(vehicles);
  const poolCars = activeVehicles.filter(v => v.poolcar);
  const privateVehicles = activeVehicles.filter(v => !v.poolcar);
  
  // Berechne Summe aller monatlichen Netto-Kosten (nur aktive Fahrzeuge)
  const totalMonthlyCost = activeVehicles.reduce((sum, v) => {
    const netCost = parseFloat(v.monthly_net_sum || '0');
    return sum + netCost;
  }, 0);

  // Berechne Summe aller monatlichen Brutto-Kosten (nur aktive Fahrzeuge)
  const totalMonthlyGrossCost = activeVehicles.reduce((sum, v) => {
    const grossCost = parseFloat(v.monthly_gross_sum || '0');
    return sum + grossCost;
  }, 0);

  // Versteuerungs-Statistiken
  const taxationStats = activeVehicles.reduce((stats, vehicle) => {
    const taxation = vehicle.taxation || 'Keine Versteuerung';
    stats[taxation] = (stats[taxation] || 0) + 1;
    return stats;
  }, {} as Record<string, number>);

  // Berechne ablaufende Verträge
  const expiringContracts = activeVehicles.filter(v => {
    if (!v.end_of_contract) return false;
    const endDate = new Date(v.end_of_contract);
    const today = new Date();
    const daysUntilEnd = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilEnd <= 90 && daysUntilEnd > 0;
  });

  // Berechne ablaufende TÜV
  const expiringTuv = activeVehicles.filter(v => {
    if (!v.tuev_until) return false;
    const tuevDate = new Date(v.tuev_until);
    const today = new Date();
    const daysUntilTuev = Math.ceil((tuevDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilTuev <= 30 && daysUntilTuev > 0;
  });

  // Versteuerungs-Übersicht
  const taxationOverview = {
    noTaxation: taxationStats['Keine Versteuerung'] || 0,
    combustion1: taxationStats['1% Verbrenner'] || 0,
    hybrid05: taxationStats['0,5% Hybrid'] || 0,
    electric025: taxationStats['0,25% Elektro'] || 0
  };

  // Lade Schadensdaten beim Mount
  useEffect(() => {
    const loadDamageReports = async () => {
      try {
        setLoading(true);
        const response = await baserowApi.getDamageReports();
        setDamageReports(response.results);
      } catch (error) {
        console.error('Fehler beim Laden der Schadensdaten:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDamageReports();
  }, []);

  // Berechne Gesamtkosten der Schäden und Kategorien-Statistiken
  useEffect(() => {
    if (damageReports.length === 0) return;

    const currentYear = new Date().getFullYear();
    let totalCosts = 0;
    const categoryCounts: Record<string, number> = {
      'Schaden': 0,
      'Termin': 0,
      'Service & Inspektion': 0
    };

    damageReports.forEach(report => {
      let reportYear = currentYear;
      
      if (report.created_at) {
        const reportDate = new Date(report.created_at);
        const calculatedYear = reportDate.getFullYear();
        
        if (!isNaN(calculatedYear)) {
          reportYear = calculatedYear;
        }
      }
      
      // Extrahiere Kategorie aus Beschreibung
      const category = extractCategoryFromDescription(report.description || '');
      
      // Nur Projekte mit gültiger Kategorie berücksichtigen
      if (category && reportYear === currentYear) {
        categoryCounts[category] = (categoryCounts[category] || 0) + 1;
        
        // Nur echte "Schäden" für Kostenberechnung verwenden
        if (category === 'Schaden') {
          const realCost = parseFloat(report.real_costs) || 0;
          totalCosts += realCost;
        }
      }
    });

    setTotalDamageCosts(totalCosts);
    setTotalProjectsByCategory(categoryCounts);
  }, [damageReports]);

  return (
    <>
      <div className="mb-6">
        {/* Erste Reihe - Hauptstatistiken */}
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="bg-white rounded-lg shadow-md p-4 h-24 flex items-center">
            <div className="flex items-center w-full">
              <div className="p-2 rounded-full bg-[#add015] bg-opacity-20">
                <Car className="w-5 h-5 text-[#add015]" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Gesamtfahrzeuge</p>
                <p className="text-2xl font-bold text-gray-900">{activeVehicles.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-4 h-24 flex items-center">
            <div className="flex items-center w-full">
              <div className="p-2 rounded-full bg-gray-100">
                <Euro className="w-5 h-5 text-gray-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Monatliche Kosten</p>
                <p className="text-xl font-bold text-gray-900">{totalMonthlyCost.toFixed(2)} €</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-4 h-24 flex items-center">
            <div className="flex items-center w-full">
              <div className="p-2 rounded-full bg-[#add015] bg-opacity-20">
                <Users className="w-5 h-5 text-[#add015]" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Poolcars</p>
                <p className="text-2xl font-bold text-gray-900">{poolCars.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-4 h-24 flex items-center">
            <div className="flex items-center w-full">
              <div className="p-2 rounded-full bg-gray-100">
                <User className="w-5 h-5 text-gray-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Firmenfahrzeuge</p>
                <p className="text-2xl font-bold text-gray-900">{privateVehicles.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Zweite Reihe - Projekt-Kategorien */}
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="bg-white rounded-lg shadow-md p-4 h-24 flex items-center">
            <div className="flex items-center w-full">
              <div className="p-2 rounded-full bg-red-100">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Schäden</p>
                <p className="text-2xl font-bold text-gray-900">{totalProjectsByCategory['Schaden'] || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-4 h-24 flex items-center">
            <div className="flex items-center w-full">
              <div className="p-2 rounded-full bg-[#add015] bg-opacity-20">
                <Calendar className="w-5 h-5 text-[#add015]" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Termine</p>
                <p className="text-2xl font-bold text-gray-900">{totalProjectsByCategory['Termin'] || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-4 h-24 flex items-center">
            <div className="flex items-center w-full">
              <div className="p-2 rounded-full bg-gray-100">
                <Shield className="w-5 h-5 text-gray-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Service & Inspektion</p>
                <p className="text-2xl font-bold text-gray-900">{totalProjectsByCategory['Service & Inspektion'] || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-4 h-24 flex items-center">
            <div className="flex items-center w-full">
              <div className="p-2 rounded-full bg-red-100">
                <Euro className="w-5 h-5 text-red-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Schadenskosten 2025</p>
                <p className="text-xl font-bold text-gray-900">{totalDamageCosts.toFixed(2)} €</p>
              </div>
            </div>
          </div>
        </div>

        {/* Dritte Reihe - Weitere Statistiken */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-md p-4 h-24 flex items-center">
            <div className="flex items-center w-full">
              <div className="p-2 rounded-full bg-gray-100">
                <Calendar className="w-5 h-5 text-gray-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Verträge laufen ab</p>
                <p className="text-2xl font-bold text-gray-900">
                  {expiringContracts.length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-4 h-24 flex items-center">
            <div className="flex items-center w-full">
              <div className="p-2 rounded-full bg-gray-100">
                <Shield className="w-5 h-5 text-gray-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">TÜV läuft ab</p>
                <p className="text-2xl font-bold text-gray-900">
                  {expiringTuv.length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-4 h-24 flex items-center">
            <div className="flex items-center w-full">
              <div className="p-2 rounded-full bg-[#add015] bg-opacity-20">
                <TrendingUp className="w-5 h-5 text-[#add015]" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Bruttokosten</p>
                <p className="text-xl font-bold text-gray-900">{totalMonthlyGrossCost.toFixed(2)} €</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StatsOverview; 